/**
 * AssignProcessModal — Globalne typy dokumentów, Etap 4 (tenant actions).
 *
 * Mały modal do przypisania/zmiany domyślnego procesu (obiegu) na typie globalnym
 * w kontekście jednej firmy (nadpisanie tenantowe — `document_type_overrides`).
 * Wzorowany na prostym `<select>` z `DocumentTypeFormModal` (pole „Domyślny obieg
 * akceptacji"), NIE na `components/processes/ProcessPickerModal.tsx` (ten drugi jest
 * zorientowany na komentarze/uruchamianie procesu na rekordzie — inny cel).
 *
 * Rodzic dostarcza listę opublikowanych procesów (już przefiltrowanych po
 * `targetModuleCode='crm_core.documents'`) oraz aktualnie przypisany proces
 * (`currentProcessId`, `null` = brak). `onConfirm` dostaje wybrane id albo `null`
 * (wyczyszczenie wyboru) — rodzic woła `PUT .../override` z `defaultProcessId`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';

export interface AssignProcessOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  processes: AssignProcessOption[];
  currentProcessId: string | null;
  submitting?: boolean;
  onConfirm: (processId: string | null) => void;
  onClose: () => void;
}

export function AssignProcessModal({
  open,
  processes,
  currentProcessId,
  submitting,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(currentProcessId ?? '');

  useEffect(() => {
    if (open) {
      setSelected(currentProcessId ?? '');
    }
  }, [open, currentProcessId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.documentTypes.assignProcess.title')}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="assign-process-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(selected.trim() ? selected : null)}
            loading={submitting}
            data-testid="assign-process-confirm"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormField label={t('tenant.documentTypes.assignProcess.processLabel')}>
        <Dropdown
          value={selected}
          onChange={(v) => setSelected(v)}
          data-testid="assign-process-select"
          options={[
            { value: '', label: t('tenant.recordTypes.defaultProcess.none') },
            ...processes.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
      </FormField>
    </Modal>
  );
}
